import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import { useTheme } from '@mui/material/styles'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import React from 'react'
import { Control, Controller } from 'react-hook-form'
import { ProductAvailabilitiesFormValues, ProductAvailabilityCategory, ProductType } from '../types'

type ExpressCheckoutProductAccordionProps = {
  category: ProductAvailabilityCategory
  control: Control<ProductAvailabilitiesFormValues>
  productValues: Record<string, boolean>
  disabled: boolean
  productType: ProductType
  onToggle?: (productId: string, value: boolean) => void
}
export function ExpressCheckoutProductAccordion({
  category,
  control,
  productValues,
  disabled,
  productType,
  onToggle,
}: ExpressCheckoutProductAccordionProps) {
  if (!hasProductsOrSubcategories(category)) {
    return null
  }

  const theme = useTheme()

  const SmallPrimaryTriangle = () => (
    <svg width='16' height='16' viewBox='0 0 16 16'>
      <polygon points='8,12 4,6 12,6' fill={theme.palette.primary.main} />
    </svg>
  )

  const unavailableProductsCount = getUnavailableProductsCount(productValues, category)

  return (
    <Accordion>
      <AccordionSummary expandIcon={<SmallPrimaryTriangle />} aria-controls={`panel-content`} id={`panel-header`}>
        <Grid container alignItems='center' spacing={1}>
          <Typography>{category.name}</Typography>
          {unavailableProductsCount > 0 && (
            <Grid>
              <Avatar
                sx={{
                  bgcolor: theme.palette.warning.main,
                  width: 22,
                  height: 22,
                  fontSize: 13,
                  fontWeight: 'bold',
                }}
              >
                {unavailableProductsCount}
              </Avatar>
            </Grid>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid sx={{ mb: 1 }}>
          {category.products?.map((product) => (
            <Grid key={product.productId}>
              <Grid container alignItems='center'>
                <Grid>
                  <Controller
                    name={
                      productType === ProductType.MODIFIER
                        ? `modifiers.${product.productId}`
                        : `products.${product.productId}`
                    }
                    control={control}
                    render={({ field }) => (
                      <Switch
                        color='primary'
                        checked={!!field.value}
                        onChange={(e) => {
                          onToggle?.(product.productId, e.target.checked)
                          field.onChange(e)
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Typography variant='body1'>{product.name || '-'}</Typography>
                  <Typography variant='body2' style={{ color: theme.palette.text.secondary }}>
                    {product.productId}
                    {product.productId && product.ean ? ' | ' : ''}
                    {product.ean}
                  </Typography>
                </Grid>
              </Grid>
              <Divider variant='middle' sx={{ mt: 1, mb: 1 }} />
            </Grid>
          ))}
        </Grid>
        <Grid>
          {category.categories?.map((subCategory) => (
            <ExpressCheckoutProductAccordion
              key={subCategory.name}
              category={subCategory}
              control={control}
              productValues={productValues}
              disabled={disabled}
              productType={productType}
              onToggle={onToggle}
            />
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

const hasProductsOrSubcategories = (category: ProductAvailabilityCategory): boolean => {
  return (category.products && category.products.length > 0) || !!category.categories?.some(hasProductsOrSubcategories)
}

const getUnavailableProductsCount = (
  productValues: Record<string, boolean>,
  category: ProductAvailabilityCategory,
): number => {
  if (!productValues) return 0
  let count = category.products?.filter((product) => productValues[product.productId] === false).length ?? 0
  if (category.categories && Array.isArray(category.categories)) {
    for (const subCategory of category.categories) {
      count += getUnavailableProductsCount(productValues, subCategory)
    }
  }
  return count
}
